/**
 * /table · Home / invitation view.
 *
 * Hero · search · promise strip.
 * Staggered rise animation (delays 0.05s → 0.35s).
 */
import React from "react";
import { SearchField } from "./SearchField";
import { ExamplePrompts } from "./ExamplePrompts";
import { RecentSearches } from "./RecentSearches";

export function TableHome({ q, setQ, onRun, onAskConcierge }) {
    return (
        <main
            data-testid="table-home"
            className="max-w-3xl mx-auto px-6 pt-20 pb-40 text-center"
        >
            <div className="t-rise" style={{ animationDelay: "0.05s" }}>
                <span
                    data-testid="hero-eyebrow"
                    className="t-mono uppercase"
                    style={{
                        fontSize: 11,
                        letterSpacing: "0.34em",
                        color: "var(--t-brass)",
                    }}
                >
                    global · verified · booking-first
                </span>
                <h1
                    data-testid="hero-heading"
                    className="t-serif mt-6"
                    style={{
                        fontSize: "clamp(40px, 7vw, 68px)",
                        lineHeight: 1.02,
                        fontWeight: 400,
                        letterSpacing: "-0.02em",
                        margin: "24px 0 0",
                    }}
                >
                    Name the place.<br />
                    <span style={{ fontStyle: "italic", color: "var(--t-brass)" }}>
                        We hold the table.
                    </span>
                </h1>
                <p
                    className="mt-6 mx-auto"
                    style={{
                        maxWidth: 520,
                        fontSize: 17,
                        lineHeight: 1.6,
                        color: "var(--t-ink2)",
                    }}
                >
                    Search any restaurant on earth. We route you to the fastest
                    verified way to book — and where there isn&apos;t one, the Concierge®
                    team secures it for you. Nothing invented, ever.
                </p>
            </div>

            <div className="t-rise mt-12" style={{ animationDelay: "0.15s" }}>
                <SearchField
                    value={q}
                    onChange={setQ}
                    onSubmit={onRun}
                    large
                />
            </div>

            {/* Example prompts · 6 lyrical click-to-fire chips · gives
               members permission to type naturally. */}
            <ExamplePrompts
                onPick={(prompt) => {
                    setQ(prompt);
                    onRun?.(prompt);
                }}
            />

            {/* Recent searches · localStorage-backed strip · only
               renders if the member has ever searched before. */}
            <RecentSearches
                onPick={(q2) => {
                    setQ(q2);
                    onRun?.(q2);
                }}
            />

            {/* Ask the Concierge® — secondary hero CTA (parity with /escape
               and /enjoy heroes; founder direction 27 Jun 2026). Opens a
               generic concierge request — no venue required. */}
            {onAskConcierge ? (
                <div
                    className="t-rise"
                    style={{
                        animationDelay: "0.25s",
                        display: "flex",
                        justifyContent: "center",
                        marginTop: 18,
                    }}
                >
                    <button
                        type="button"
                        data-testid="table-hero-ask-concierge"
                        onClick={() => onAskConcierge(q)}
                        style={{
                            background: "transparent",
                            color: "var(--t-brass)",
                            border: "1px solid var(--t-brass)",
                            borderRadius: 999,
                            padding: "10px 22px",
                            fontSize: 13.5,
                            fontWeight: 500,
                            cursor: "pointer",
                            fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
                            transition: "all 0.15s",
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = "var(--t-brass)";
                            e.currentTarget.style.color = "var(--t-paper)";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = "transparent";
                            e.currentTarget.style.color = "var(--t-brass)";
                        }}
                    >
                        Ask the Concierge®
                    </button>
                </div>
            ) : null}

            {/* Intent-chip row removed 9 Feb 2026 (founder direction:
               "remove these chips too they dont make any sense"). The
               SearchField is the single intent surface · members type
               naturally ("Best bars in Dubai" · "Veg Michelin London")
               and the LLM query parser routes them. Filter chips live
               on the browse view inside BrowseSearchBar. */}

            {/* Promise strip (Instant / Direct / Concierge®) removed
               30 Jun 2026 as part of the parity sprint · Maison-style
               clean hero, no icon rail. The Concierge® voice line
               speaks for itself inside the drawer. */}
        </main>
    );
}
